import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import PointPreloader from '@App/kit/components/Preloaders/PointPreloader'
import { StoresContext } from '@App/contexts'
import SearchPanel from '@Components/Search/SearchPanel'
import ClaimSearchPaginationTable from '@Scenes/RightsholderScene/MatchClaim/Claim/ClaimSearch/ClaimSearchPaginationTable'
import { ClaimSearchFilter, ClaimSearchFilterCollection } from '@App/forms/filters/claim-search-filter'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import WizardStepHeading from '@Components/WizardFlow/WizardStepHeading'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import useClaimSearch from '@Scenes/RightsholderScene/MatchClaim/Claim/ClaimSearch/hooks/useClaimSearch'

interface ClaimSearchProps {
    selectWork: (id: number | null | undefined) => void
}

const ClaimSearch = (props: ClaimSearchProps) => {
    const { selectWork } = props
    const { claimingToolSearchStore } = useContext(StoresContext)
    // Only pre-populates the filter UI for whenever the user submits their own search terms —
    // Claiming Tool never auto-searches on load, so there's no initial fetch to seed here.
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<ClaimSearchFilter>(ClaimSearchFilterCollection)
    // A link can also carry search terms (?combinedTitles=…). They must be in the store before the form first
    // renders — it picks its rows up then — so this runs in a state initializer, not an effect.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        claimingToolSearchStore.searchCriteriaStore.criteriaOptions
    )
    useState(() => {
        if (urlCriteria) {
            claimingToolSearchStore.seedSearchFromUrl(urlCriteria, urlFilterValues as ClaimSearchFilter | undefined)
        }
    })
    const { claimQuery, search, onChangePage, onChangeLimit } = useClaimSearch()

    useEffect(() => {
        analyticsSendPage('rightsholder_claim_search')
    }, [])

    return (
        <>
            <title>Claiming Tool</title>
            <div className="claim-search-wrapper">
                <WizardStepHeading heading="Search">
                    {'Use the search fields below to locate works with unclaimed shares within our public database. You can enter up\n' +
                        '        to 5 search criteria to locate works with unclaimed shares.'}
                </WizardStepHeading>
                <div className="claiming-tool">
                    {claimingToolSearchStore.searchCriteriaStore && (
                        <SearchPanel
                            searchCriteriaStore={claimingToolSearchStore.searchCriteriaStore}
                            canAddCriteria
                            onSearch={(searchCriteria, filters) => {
                                syncCriteriaToUrl(searchCriteria)
                                search(searchCriteria, filters)
                            }}
                            filterFieldCollections={ClaimSearchFilterCollection}
                            defaultFilterValues={urlFilterValues}
                            onFilterValueChange={syncFiltersToUrl}
                            disabled={claimQuery.isFetching}
                        />
                    )}
                    {claimQuery.isFetching ? (
                        <PointPreloader />
                    ) : (
                        <ClaimSearchPaginationTable
                            selectWork={selectWork}
                            limit={claimingToolSearchStore.limit}
                            onChangePage={onChangePage}
                            onChangeLimit={onChangeLimit}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

export default observer(ClaimSearch)
